// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import MagnifyIcon from '@material-ui/icons/Search';

import AnalyticsBase from '../AnalyticsBase';
import AnalyticsFilterBar from '../AnalyticsFilterBar';
import EmptyState from '../../common/EmptyState';
import BreadcrumbNav from '../../nav/BreadcrumbNav';

import { initializeAnalytics } from '../../../state/reducer/analytics/actionCreators/initializeAnalytics';
import { ParamUrlBuilder } from '../../../lib/ParamUrlBuilder';
import * as DateUtils from '../../../lib/dateUtils';
import * as LibraryState from '../../../state/reducer/library/library';
import * as AnalyticsState from '../../../state/reducer/analytics/analytics';
import * as AppState from '../../../state/reducer/app/appState';

import Console from '../../../lib/debug';

type Props = {
    match: any,
    dispatch: any => void
};

const PAGE_KEY = 'training-summary-athlete';
export const DEFAULT_SEARCH_DATE_OFFSET = 30;

export class TrainingSummaryAthleteViewStandalone extends Component<Props> {
    constructor(props: Props) {
        super(props);
        LibraryState.fetchAthletes();
    }

    componentDidUpdate(prevProps: {}, prevState: {}) {
        this.loadAnalytics();
    }

    loadAnalytics() {
        const dataParams = this.getDataParams();
        if (dataParams.user_ids.length === 0) {
            AppState.stopLoadingAnalytics();
            return;
        }

        this.props.dispatch(initializeAnalytics(PAGE_KEY, dataParams));
    }

    getAthleteId = () => {
        const params = this.getDataParams();
        if (params.user_ids) {
            return parseInt(params.user_ids, 10);
        }
        return undefined;
    };

    getStartDate = () => {
        const dataParams = this.getDataParams();
        return moment(dataParams.date_start);
    };

    getEndDate = () => {
        const dataParams = this.getDataParams();
        return moment(dataParams.date_end);
    };

    onSelect = (e: Event, { suggestion }: any) => {
        const { id } = suggestion;
        const dataParams = this.getDataParams();
        const paramUrlBuilder = new ParamUrlBuilder(
            '/analytics/training-summary-athlete/'
        );
        paramUrlBuilder
            .setUserId(id)
            .setStartDate(this.getStartDate())
            .setEndDate(this.getEndDate())
            .setUseMetric(dataParams.use_metric);

        AnalyticsState.navigate(paramUrlBuilder.buildUrl());
    };

    getInitialState = () => {
        const athleteId = this.getAthleteId();
        if (athleteId) {
            return null;
        }

        return (
            <div style={{ paddingTop: 60 }}>
                <EmptyState
                    text="Select Athlete to Run Training Summary Analytics"
                    icon={<MagnifyIcon style={{ fontSize: 81 }} />}
                />
            </div>
        );
    };

    setSearchParams = (params: {}) => {
        const existingParams = AnalyticsState.getSearchParams();
        AnalyticsState.updateSearchParams({
            ...existingParams,
            ...params
        });
    };

    getDataParams = () => {
        const searchParams = AnalyticsState.getSearchParams();
        let endDate = searchParams.end_date;
        if (!DateUtils.isValidDateString(endDate)) {
            endDate = moment();
            endDate = moment(endDate, DateUtils.CALENDAR_FORMAT);
        }

        // Always subtract 30 days from the end date if the start date
        // isn't provided.
        let startDate = searchParams.start_date;
        if (!DateUtils.isValidDateString(startDate)) {
            startDate = moment(endDate).subtract(
                DEFAULT_SEARCH_DATE_OFFSET,
                'd'
            );
            startDate = moment(startDate, DateUtils.CALENDAR_FORMAT);
        }

        let userIds = searchParams.user_id;
        let userIdArray = [];
        if (userIds) {
            userIdArray.push(userIds);
        }
        let useMetric = parseInt(searchParams.use_metric, 10) === 1;
        if (searchParams.use_metric === undefined) {
            useMetric = this.props && this.props.useMetric;
        }

        return {
            date_start: moment(startDate).format('YYYY-MM-DD'),
            date_end: moment(endDate).format('YYYY-MM-DD'),
            user_ids: userIdArray,
            use_metric: useMetric
        };
    };

    handleDateChange = ({ startDate, endDate }: State) => {
        this.setSearchParams({
            start_date: startDate.format(DateUtils.CALENDAR_FORMAT),
            end_date: endDate.format(DateUtils.CALENDAR_FORMAT)
        });
    };

    handleUseMetricChange = (useMetric: boolean) => {
        this.setSearchParams({
            use_metric: useMetric ? 1 : 0
        });
    };

    getPageTitle = () => {
        let title =
            this.props.metaTree &&
            this.props.metaTree.instances &&
            this.props.metaTree.instances[PAGE_KEY] &&
            this.props.metaTree.instances[PAGE_KEY].title;
        if (title) {
            return title;
        }
        return '';
    };

    render() {
        Console.render('TrainingSummaryAthleteView');
        const initialState = this.getInitialState();

        const crumbs = [
            {
                displayString: 'Analytics',
                link: `/analytics`,
                editable: false
            },
            {
                displayString: this.getPageTitle(),
                link: null,
                editable: false
            }
        ];

        const dataParams = this.getDataParams();

        return (
            <div className="has-filter-bar">
                <BreadcrumbNav crumbs={crumbs} />
                <AnalyticsFilterBar
                    dateRangePicker={{
                        startDate: this.getStartDate(),
                        endDate: this.getEndDate(),
                        onChange: this.handleDateChange
                    }}
                    athleteSelector={{
                        selectedId: this.getAthleteId(),
                        onSelect: this.onSelect
                    }}
                    useMetricSelector={{
                        useMetric: dataParams.use_metric,
                        onChange: this.handleUseMetricChange
                    }}
                />
                <AnalyticsBase includeGraph={true} />
                {initialState}
            </div>
        );
    }
}

const mapStateToProps = (state: any, ownProps: any) => {
    return {
        ...ownProps,
        athletesTree: state.library.athletesTree,
        metaTree: state.analytics.metaTree,
        useMetric: state.app.loggedInUser.use_metric
    };
};

export default connect(mapStateToProps)(TrainingSummaryAthleteViewStandalone);
